<!-- Ported from the Polaris kit: src/product/components/PaginationFooter.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase P, 28/09):
     - `noun` ("items") becomes two i18n keys with the plural, so each list says its own noun in en and pt_BR:
       `countKey` ("No items | {count} item | {count} items": no rows, one page) and `showingKey`
       ("Showing {range} of {total} items", `{range}` highlighted as in the kit). Defaults POLARIS.PAGINATION_FOOTER.*.
     - Numbers in the viewer's locale (useNumberFormatter), not the kit's fixed 'en-US'.
     - Not the woot's components-next/pagination/PaginationFooter.vue (untouched): the area that lists decides
       whether to swap it (its props are `currentPage`/`totalItems`/`itemsPerPage`; these are `page`/`total`/`pageSize`). -->
<script setup>
// The foot of a long list: which rows are showing out of how many, and Previous and Next with the page you are on. A composition of the
// registry Pagination. With one page it says only how many there are; with none it says there are none.
import { computed } from 'vue';
import { Translation, useI18n } from 'vue-i18n';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';
import {
  Pagination,
  PaginationContent,
  PaginationNext,
  PaginationPrevious,
} from '../ui/pagination';

const props = defineProps({
  page: { type: Number, default: 1 }, // v-model:page
  total: { type: Number, required: true }, // how many rows in all
  pageSize: { type: Number, default: 16 },
  countKey: { type: String, default: 'POLARIS.PAGINATION_FOOTER.COUNT' },
  showingKey: { type: String, default: 'POLARIS.PAGINATION_FOOTER.SHOWING' },
});
const emit = defineEmits(['update:page']);
const { t } = useI18n();
const { formatFullNumber: number } = useNumberFormatter();

const pages = computed(() =>
  Math.max(1, Math.ceil(props.total / props.pageSize))
);
const from = computed(() =>
  props.total ? (props.page - 1) * props.pageSize + 1 : 0
);
const to = computed(() => Math.min(props.page * props.pageSize, props.total));
</script>

<template>
  <div
    class="flex flex-wrap items-center justify-between gap-3"
    data-pagination-footer
  >
    <p
      class="type-polaris-caption text-polaris-muted-foreground"
      aria-live="polite"
    >
      <template v-if="pages === 1">
        {{ t(countKey, { count: number(total) }, total) }}
      </template>
      <Translation
        v-else
        :keypath="showingKey"
        :plural="total"
        tag="span"
        scope="global"
      >
        <template #range>
          <span class="text-polaris-foreground">
            {{
              t('POLARIS.PAGINATION_FOOTER.RANGE', {
                from: number(from),
                to: number(to),
              })
            }}
          </span>
        </template>
        <template #total>{{ number(total) }}</template>
      </Translation>
    </p>
    <Pagination
      v-if="pages > 1"
      v-slot="{ page: current }"
      :page="page"
      :total="total"
      :items-per-page="pageSize"
      :sibling-count="0"
      class="mx-0 w-auto"
      @update:page="value => emit('update:page', value)"
    >
      <PaginationContent>
        <PaginationPrevious />
        <span
          class="px-2 type-polaris-caption text-polaris-muted-foreground"
          aria-live="polite"
        >
          {{
            t('POLARIS.PAGINATION_FOOTER.PAGE', {
              page: number(current),
              pages: number(pages),
            })
          }}
        </span>
        <PaginationNext />
      </PaginationContent>
    </Pagination>
  </div>
</template>
